import * as React from "react";
import {Box, Grid, InputAdornment, TextField, Button, Stack, Paper, Dialog, DialogTitle, DialogContent, IconButton} from "@mui/material";
import {Autocomplete} from "@mui/lab";
import * as _ from "lodash";
import SearchIcon from "@mui/icons-material/Search";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {AdminAPI} from "../admin_api";
import makeStyles from "@mui/styles/makeStyles";
import {ColumnTypes} from "./table_row";
import Formatter from "../../../util/formatter";
import ColumnsManager, {getColumns} from "../hl7/columns_manager";
import {agylePref} from "../../../store/reducers/preferences";
import AgyleAdminTable from "./table";
import CompareIcon from '@mui/icons-material/Compare';
import DifferenceIcon from '@mui/icons-material/Difference';
import CloseIcon from '@mui/icons-material/Close';
import ReactJsonViewCompare from 'react-json-view-compare';
import ReactDiffViewer from 'react-diff-viewer';
import {setLoading} from "../../../store/reducers/patient";

const useStyles = makeStyles((theme) => ({
    selectApi: {
        display: "inline-flex",
        flexDirection: "column",
        margin: "8px 0px"
    },
    diffViewer: {
        //color: theme.palette.tertiary.contrastText,
        overflowWrap: "anywhere"
    }
}));

const columnsWidths = {
    "Info": '100px',
    "Date": '300px',
    "Created": '300px',
    "Type": '170px',
    "Hospital number": '170px',
    "Attendance": '200px',
    "System": '200px',
    "Version": '200px',
    "Actions": '200px',
    "Patient": '200px',
    "Status": '170px',
    "Last Send Time": '200px',
    "Last Status": '170px',
    "Hl7 Type": '170px',
};

function DicAudit(props) {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [apis, setApis] = React.useState([]);
    const [chooseApi, setChooseApi] = React.useState(null);
    const [searchVal, setSearchVal] = React.useState("");
    const [recordsReady, setRecordsReady] = React.useState(false);
    const [records, setRecords] = React.useState([]);
    const [openConfig, setOpenConfig] = React.useState(false);
    const [columns, setColumns] = React.useState([]);
    const [showCompareDlg, setShowCompareDlg] = React.useState(false);
    const [selectedRecord, setSelectedRecord] = React.useState(null);
    const [showCompare, setShowCompare] = React.useState(false);
    const [showChanges, setShowChanges] = React.useState(false);
    const agylePreference = useSelector(agylePref);
    const controller = new AbortController();
    const [sort, setSort] = React.useState('"updatedAt":-1');

    React.useEffect(()=>{
        setColumns(getColumns(columnsInit, agylePreference?.dictAuditColumnsOrder));
        },[agylePreference]);

    React.useEffect(()=>{
        let apis = [{label: AdminAPI.DefaultApiName, conf: AdminAPI.DefaultApiName}];

        _.forEach(props.dictionariesEditors, dictConf => {
            if(dictConf && dictConf.hasOwnProperty("apiConfigName")){
                if(apis.indexOf(dictConf.apiConfigName) < 0){
                    apis.push({label: dictConf.apiConfigName, conf: dictConf.apiConfigName});
                }
            }
        })

        setApis(apis);
        return () => {}
    },[]);

    React.useEffect(()=>{
        //tableName, signal, filter, page, pagesize, sort, api= AdminAPI.DefaultApiName
        if(chooseApi != null) {
            setRecordsReady(false);
            AdminAPI.getTableData("adminAudits", controller.signal, null, 0, 1000, sort, chooseApi.conf).then(
                (records) => {
                    setRecords(records);
                    setTimeout(()=>{
                        setRecordsReady(true);
                        dispatch(setLoading(false));
                    }, 100);
                }
            )
        } else {
            setRecords([]);
            setRecordsReady(false);
            dispatch(setLoading(false));
        }
        return () => {
            controller.abort();
            dispatch(setLoading(false));
        }
    },[chooseApi]);

    React.useEffect(() => {
        if(chooseApi != null) {
            if (searchVal && searchVal.length > 0) {
                let filter = "";
                const _searchString = getStringIdOrObjId(searchVal);
                if (_.isObject(_searchString)) {
                    filter = {referenceId: _searchString};
                } else {
                    filter = {
                        $or: [{referenceId: {$regex: `.*${_searchString}.*`, $options: 'i'}},
                            {referenceType: {$regex: `.*${_searchString}.*`, $options: 'i'}}]
                    };
                }
                AdminAPI.getTableData("adminAudits", controller.signal, filter, 0, 1000, sort, chooseApi.conf).then(
                    (records) => {
                        setRecords(records);
                        setTimeout(() => {
                            setRecordsReady(true);
                            dispatch(setLoading(false));
                        }, 100);
                    }
                )
            } else {
                AdminAPI.getTableData("adminAudits", controller.signal, null, 0, 1000, sort, chooseApi.conf).then(
                    (records) => {
                        setRecords(records);
                        setTimeout(() => {
                            setRecordsReady(true);
                            dispatch(setLoading(false));
                        }, 100);
                    }
                )
            }
        }
    }, [searchVal]);

    const getStringIdOrObjId = (data) => {
        if(data && typeof(data) === "string" && data.startsWith("$objectid(")) {
            let _reg  = /\$objectid\("?([0-9a-z]+)/;
            let test = data.match(_reg);
            if(test && test.length>1) {
                return {$oid:test[1]};
            }
        }
        return data;
    }

    const handlerShowCompare = (row) => {
        setSelectedRecord(row);
        setShowCompareDlg(true);
        setShowCompare(true);
    }

    const handlerShowChanges = (row) => {
        setSelectedRecord(row);
        setShowCompareDlg(true);
        setShowChanges(true);
    }

    const handleCloseDialog = () => {
        setShowCompareDlg(false);
        setShowCompare(false);
        setShowChanges(false);
        setSelectedRecord(null);
    }

    const columnsInit = [
        {
            name: 'updatedAt',
            order: 1,
            value: 'updatedAt',
            type: ColumnTypes.TEXT,
            withMessage: true,
            description: 'Date',
            minWidth: 200,
            maxWidth: 200,
            format: (value) => {
                if(typeof value === "object" && value.hasOwnProperty("$date") && value["$date"].hasOwnProperty("$numberLong")){
                    let data = Formatter.utcAndLocalFormatData(value["$date"]["$numberLong"],"YYYY-MM-DD HH:mm:ss", "", true);;
                    return (<span>
                        {(data.split("|")).map((dItem, index) => {
                            return (<div key={index}>
                                {dItem}
                            </div>)
                        })}
                    </span>)
                } else {
                    return "---"
                }
                //return Formatter.utcAndLocalFormatData(value,"YYYY-MM-DD HH:mm:ss", "");
            }},
        {
            name: 'changedBy',
            order: 1,
            value: 'changedBy',
            type: ColumnTypes.TEXT,
            description: 'Changed By',
            minWidth: 110 },
        {
            name: 'changeType',
            order: 1,
            value: 'changeType',
            type: ColumnTypes.TEXT,
            description: 'Action',
            minWidth: 86 },
        {
            name: 'referenceId' ,
            order: 1,
            value: 'referenceId',
            type: ColumnTypes.TEXT,
            description: 'Reference ID',
            minWidth: 120,
            format: (value) => {
                if(typeof value === "object"){
                    return JSON.stringify(value);
                } else {
                    return value
                }
                //return Formatter.utcAndLocalFormatData(value,"YYYY-MM-DD HH:mm:ss", "");
            }
        },
        {
            name: 'referenceType',
            order: 1,
            value: 'referenceType',
            type: ColumnTypes.TEXT,
            description: 'Reference Type',
            minWidth: 134 },
        {
            name: 'newValue',
            order: 1,
            value: 'newValue',
            type: ColumnTypes.TEXT,
            description: 'New Value',
            minWidth: 100,
            maxWidth: "28%",
            format: (value) => {
                try {
                    return JSON.stringify(value);
                } catch (ex) {
                    console.log(ex);
                    return "---error---"
                }
            }
        },
        {
            name: 'oldValue',
            order: 1,
            value: 'oldValue',
            type: ColumnTypes.TEXT,
            description: 'Old Value',
            minWidth: 100,
            maxWidth: "28%",
            format: (value) => {
                try {
                    return JSON.stringify(value);
                } catch (ex) {
                    console.log(ex);
                    return "---error---"
                }
            }
        },
        {
            name: 'actions',
            order: 1,
            value: 'actions',
            type: ColumnTypes.CUSTOM,
            renderer: (row) => {
                return (
                    <Stack spacing={2} direction="row">
                        <Button variant="contained" title={t("Compare")} onClick={()=>handlerShowCompare(row)}><CompareIcon/></Button>
                        <Button variant="contained" title={t("Show changes")} onClick={()=>handlerShowChanges(row)}><DifferenceIcon/></Button>
                    </Stack>
                )
            },
            description: 'Actions',
            minWidth: 10
        }

    ];

    return (
        <Box sx={{ width: '100%', padding: '5px'}}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between'}}>
                <Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Autocomplete
                                className={classes.selectApi}
                                disablePortal
                                id="combo-box-choose-api"
                                options={apis}
                                sx={{ width: 300 }}
                                renderInput={(params) => <TextField {...params} label={t("Select Api")} />}
                                value={chooseApi}
                                onChange={(event, selected) => { dispatch(setLoading(true)); setChooseApi(selected);}}
                            />
                            <TextField
                                id={_.uniqueId("search_")}
                                sx={{ m: 1, width: '25ch' }}
                                variant="outlined"
                                placeholder={t("Filter records")}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment>,
                                }}
                                onChange={(event) => setSearchVal(event.target.value)}
                                defaultValue={searchVal}
                                disabled={!recordsReady}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                        </Grid>
                    </Grid>
                    <ColumnsManager prefPath={"dictAuditColumnsOrder"}
                                    columns = {columns}
                                    title={"Dictionaries audit columns order"}
                                    columnsInit = {columnsInit}
                                    openConfig={openConfig}
                                    closeModal={() => setOpenConfig(false)}/>
                    {recordsReady &&
                        <AgyleAdminTable records={records} columns={columns} canManageColumns={false}
                                         openColumnManager={()=>{ setOpenConfig(true) }} columnsWidths={columnsWidths}
                        style={{maxHeight: "calc(100vh - 200px)"}}/>
                    }
                </Box>
            </Box>
            <Dialog open={showCompareDlg} onClose={handleCloseDialog} maxWidth={"xl"} fullWidth={true}>
                <DialogTitle>
                    {showChanges && t("Changes")}
                    {showCompare && t("Compare")}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {showChanges &&
                        <ReactJsonViewCompare oldData={selectedRecord?.oldValue} newData={selectedRecord?.newValue}/>
                    }
                    {showCompare &&
                        <div className={classes.diffViewer}>
                            <ReactDiffViewer
                                oldValue={selectedRecord?JSON.stringify(selectedRecord?.oldValue, undefined, 4):""}
                                newValue={selectedRecord?JSON.stringify(selectedRecord?.newValue, undefined, 4):""}
                                splitView={true} showDiffOnly={false} useDarkTheme={window.localStorage.getItem('fortrus_agyle_theme') == "dark"}
                            />
                        </div>
                    }
                </DialogContent>
            </Dialog>
        </Box>)
}
export default DicAudit;